import React, { useState, useEffect, useCallback, useRef } from 'react'
import { getLocalApiUrl, authFetch } from '../config/api'
import { BarChart2, Plus, Link2 } from 'lucide-react'
import { defaultFromDaysAgo, toDatetimeLocalValue } from '../utils/investigationStepBrowse'
import EvidenceLinkBrowser from './EvidenceLinkBrowser'
import { formatSydneyDateTime } from '../utils/sydneyDateTime'

const P = { pri: '#0a5169', priM: '#e8f2f6' }
const fmt$ = (n) => '$' + (Number(n) ?? 0).toLocaleString()

function mapInitialSubTab(initialSubTab) {
  if (initialSubTab === 'meter') return 'meter'
  return 'evidence'
}

function mapDefaultLane(initialSubTab) {
  if (initialSubTab === 'payouts') return 'payouts'
  if (initialSubTab === 'sessions') return 'combined'
  return 'payouts'
}

/**
 * Investigation Tools → Browse: shared evidence browser + advanced meter readings.
 */
export default function CaseBrowseData({
  caseId,
  onAdded,
  defaultVenue,
  defaultMemberId,
  defaultEgmHouseNo = '',
  defaultDays = 90,
  initialSubTab = 'sessions',
  stepId = null,
  stepTitle = '',
  presetKey = 0,
}) {
  const [venues, setVenues] = useState([])
  const [venue, setVenue] = useState('')
  const [egmFilter, setEgmFilter] = useState('')
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [subTab, setSubTab] = useState(() => mapInitialSubTab(initialSubTab))
  const [meterReadings, setMeterReadings] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [selectedMeter, setSelectedMeter] = useState(new Set())
  const [adding, setAdding] = useState(false)
  const autoLoadedRef = useRef(false)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const r = await authFetch(getLocalApiUrl('/api/venues'))
        const j = await r.json()
        if (j.success && Array.isArray(j.data) && !cancelled) setVenues(j.data)
      } catch {}
    })()
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    autoLoadedRef.current = false
    setVenue('')
    setEgmFilter(defaultEgmHouseNo ? String(defaultEgmHouseNo).trim() : '')
    setSubTab(mapInitialSubTab(initialSubTab))
    const now = new Date()
    setTo(toDatetimeLocalValue(now))
    setFrom(defaultFromDaysAgo(defaultDays || 90))
    setMeterReadings([])
    setSelectedMeter(new Set())
    setError(null)
  }, [caseId, defaultEgmHouseNo, defaultDays, initialSubTab, presetKey])

  useEffect(() => {
    if (venue) return
    if (defaultVenue) {
      const match = venues.find((v) =>
        (v.venue_id || String(v.id)) === defaultVenue ||
        v.name === defaultVenue ||
        (v.extract_name && v.extract_name === defaultVenue)
      )
      if (match) setVenue(match.venue_id || String(match.id))
    }
  }, [venues, defaultVenue, venue])

  const venueFilter = venue.trim()
  const egmPkFilter = egmFilter.trim()

  const loadMeterReadings = useCallback(async () => {
    if (!venueFilter && !egmPkFilter && !from) return
    setLoading(true)
    setError(null)
    try {
      const qs = new URLSearchParams()
      if (venueFilter) qs.set('venue_id', venueFilter)
      if (egmPkFilter) qs.set('egm_pk', egmPkFilter)
      if (from) qs.set('from', from)
      if (to) qs.set('to', to)
      qs.set('limit', '200')
      const r = await authFetch(getLocalApiUrl(`/api/gaming/egms/readings?${qs}`))
      const j = await r.json()
      if (!j.success) throw new Error(j.error || 'Failed to load meter readings')
      setMeterReadings(Array.isArray(j.data) ? j.data : [])
    } catch (e) {
      setError(e.message)
      setMeterReadings([])
    } finally {
      setLoading(false)
    }
  }, [venueFilter, egmPkFilter, from, to])

  const load = useCallback(() => {
    if (subTab === 'meter') loadMeterReadings()
  }, [subTab, loadMeterReadings])

  useEffect(() => {
    if (autoLoadedRef.current) return
    if (subTab !== 'meter') return
    if (!((venueFilter || egmPkFilter) && from && to)) return
    autoLoadedRef.current = true
    loadMeterReadings()
  }, [subTab, venueFilter, egmPkFilter, from, to, loadMeterReadings, presetKey])

  const toggleMeter = (key) => {
    setSelectedMeter((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const handleAddMeterToEvidence = useCallback(async () => {
    if (!caseId || selectedMeter.size === 0) return
    const items = []
    for (const key of selectedMeter) {
      const m = meterReadings.find((x) => `${x.EgmPK}-${x.SnapshotTime}` === key)
      if (m) {
        items.push({
          type: 'meter_reading',
          payload: m,
          source_table: 'egm_meter_readings',
          SnapshotTime: m.SnapshotTime,
        })
      }
    }
    if (!items.length) return
    setAdding(true)
    try {
      const body = { items }
      if (stepId) body.step_id = stepId
      const r = await authFetch(getLocalApiUrl(`/api/cases/${caseId}/evidence/add`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      const j = await r.json()
      if (!j.success) throw new Error(j.error || 'Failed to add evidence')
      setSelectedMeter(new Set())
      onAdded?.(j.data)
    } catch (e) {
      setError(e.message)
    } finally {
      setAdding(false)
    }
  }, [caseId, stepId, selectedMeter, meterReadings, onAdded])

  const defaultLane = mapDefaultLane(initialSubTab)

  return (
    <div className="space-y-4">
      <div className="text-xs text-slate-500">
        Link payouts, gaming transactions, and jackpots to this investigation using the same browser as Link Evidence.
        {stepId ? ' Selected meter rows attach to the open investigation step.' : ''}
      </div>

      {stepId && stepTitle && (
        <div className="text-xs font-semibold rounded-md px-3 py-2" style={{ background: P.priM, color: P.pri }}>
          Attaching to step: {stepTitle}
        </div>
      )}

      <div className="flex gap-2 border-b border-slate-200 mb-2">
        <button
          type="button"
          onClick={() => { setSubTab('evidence'); setError(null) }}
          className={`flex items-center gap-1.5 py-2 px-4 text-xs font-bold rounded-t-lg ${subTab === 'evidence' ? 'bg-gray-100' : ''}`}
          style={{ color: subTab === 'evidence' ? P.pri : '#94a3b8' }}
        >
          <Link2 size={14} /> Link evidence
        </button>
        <button
          type="button"
          onClick={() => { setSubTab('meter'); setError(null); if (venueFilter || egmPkFilter) loadMeterReadings() }}
          className={`flex items-center gap-1.5 py-2 px-4 text-xs font-bold rounded-t-lg ${subTab === 'meter' ? 'bg-gray-100' : ''}`}
          style={{ color: subTab === 'meter' ? P.pri : '#94a3b8' }}
        >
          <BarChart2 size={14} /> Meter readings
        </button>
      </div>

      {error && <div className="text-sm text-red-600">{error}</div>}

      {subTab === 'evidence' && caseId && (
        <EvidenceLinkBrowser
          display="embedded"
          context={{ type: 'investigation_case', id: caseId }}
          defaultMemberNumber={defaultMemberId}
          defaultEgmHouseNo={defaultEgmHouseNo}
          defaultLane={defaultLane}
          stepId={stepId}
          showCsvTab={false}
          onLinked={onAdded}
        />
      )}

      {subTab === 'meter' && (
        <>
          <div className="card p-4">
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 items-end">
              <div>
                <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">Venue</label>
                <select className="input-field w-full text-sm" value={venue} onChange={(e) => setVenue(e.target.value)}>
                  <option value="">All venues</option>
                  {venues.map((v) => (
                    <option key={v.venue_id || v.id} value={v.venue_id || String(v.id)}>
                      {v.name}{v.extract_name ? ` (${v.extract_name})` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">EGM house / ID</label>
                <input
                  type="text"
                  className="input-field w-full text-sm font-mono"
                  placeholder="House # or machine pk"
                  value={egmFilter}
                  onChange={(e) => setEgmFilter(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">From</label>
                <input type="datetime-local" className="input-field w-full text-sm" value={from} onChange={(e) => setFrom(e.target.value)} />
              </div>
              <div>
                <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1">To</label>
                <input type="datetime-local" className="input-field w-full text-sm" value={to} onChange={(e) => setTo(e.target.value)} />
              </div>
              <div className="flex gap-2">
                <button type="button" onClick={load} className="btn-primary text-sm py-2 px-4">Apply</button>
                {selectedMeter.size > 0 && (
                  <button
                    type="button"
                    onClick={handleAddMeterToEvidence}
                    disabled={adding}
                    className="text-sm py-2 px-4 rounded-md border font-semibold flex items-center gap-1"
                    style={{ borderColor: P.pri, background: P.priM, color: P.pri }}
                  >
                    {adding ? 'Adding…' : <><Plus size={14} /> Add {selectedMeter.size}{stepId ? ' to step' : ''}</>}
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 w-10" />
                    <th className="px-3 py-2 text-left">SnapshotTime</th>
                    <th className="px-3 py-2 text-left">EgmPK</th>
                    <th className="px-3 py-2 text-left">Turnover</th>
                    <th className="px-3 py-2 text-left">CreditWin</th>
                    <th className="px-3 py-2 text-left">Stroke</th>
                    <th className="px-3 py-2 text-left">CashIn</th>
                    <th className="px-3 py-2 text-left">CashDrop</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {loading && (
                    <tr><td colSpan={8} className="px-3 py-6 text-center text-slate-500">Loading…</td></tr>
                  )}
                  {!loading && meterReadings.map((r) => {
                    const key = `${r.EgmPK}-${r.SnapshotTime}`
                    return (
                      <tr key={key} className="hover:bg-slate-50">
                        <td className="px-3 py-2">
                          <input type="checkbox" checked={selectedMeter.has(key)} onChange={() => toggleMeter(key)} />
                        </td>
                        <td className="px-3 py-2">{r.SnapshotTime ? formatSydneyDateTime(r.SnapshotTime) : '—'}</td>
                        <td className="px-3 py-2 font-mono">{r.EgmPK}</td>
                        <td className="px-3 py-2">{fmt$(r.Turnover)}</td>
                        <td className="px-3 py-2">{fmt$(r.CreditWin)}</td>
                        <td className="px-3 py-2">{r.Stroke ?? '—'}</td>
                        <td className="px-3 py-2">{fmt$(r.CashIn)}</td>
                        <td className="px-3 py-2">{fmt$(r.CashDrop)}</td>
                      </tr>
                    )
                  })}
                  {!loading && meterReadings.length === 0 && (
                    <tr><td colSpan={8} className="px-3 py-6 text-center text-slate-500">No meter readings. Select venue and/or EGM house / ID, set dates, and Apply.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
